import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useI18n } from '../i18n';
import { wsClient } from '../services/websocket';
import { useCallStore } from '../store/callStore';

type UpdateAvailableDetail = {
  requestReload: () => 'queued' | 'reloading';
};

export function UpdateAvailableBanner() {
  const { t } = useI18n();
  const location = useLocation();
  const activeCall = useCallStore((s) => s.activeCall);
  const incomingCall = useCallStore((s) => s.incomingCall);
  const [visible, setVisible] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [queued, setQueued] = useState(false);
  const [reloadFn, setReloadFn] = useState<(() => 'queued' | 'reloading') | null>(null);

  let hasPendingCallAction = false;
  try {
    hasPendingCallAction = !!sessionStorage.getItem('videocall.pendingCallAction');
  } catch {
    hasPendingCallAction = false;
  }

  const isCallCritical = !!activeCall
    || !!incomingCall
    || location.pathname === '/call'
    || location.pathname.startsWith('/call/')
    || hasPendingCallAction
    || wsClient.isExternalMode();

  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<UpdateAvailableDetail>).detail;
      if (!detail?.requestReload) return;
      setReloadFn(() => detail.requestReload);
      setReloading(false);
      setQueued(false);
      setVisible(true);
    };

    window.addEventListener('videocall:updateAvailable', handler);
    return () => window.removeEventListener('videocall:updateAvailable', handler);
  }, []);

  if (!visible) return null;

  return (
    <div className="fixed top-0 left-0 right-0 z-[60] px-3 py-2">
      <div className="max-w-4xl mx-auto bg-gray-900 text-white border border-gray-700 rounded-lg shadow-lg px-4 py-3 flex items-center justify-between gap-3">
        <div className="text-sm min-w-0">
          <div className="font-medium">{t('update.availableTitle')}</div>
          <div className="text-gray-300">
            {queued
              ? t('update.queuedDescription')
              : isCallCritical
                ? t('update.availableDuringCallDescription')
                : t('update.availableDescription')}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            type="button"
            onClick={() => {
              setVisible(false);
            }}
            className="px-3 py-1.5 text-sm rounded-md bg-gray-800 hover:bg-gray-700 border border-gray-700"
            disabled={reloading}
          >
            {t('update.notNow')}
          </button>
          <button
            type="button"
            onClick={() => {
              if (!reloadFn) return;
              try {
                const result = reloadFn();
                if (result === 'queued') {
                  setQueued(true);
                  setReloading(false);
                  return;
                }
                setQueued(false);
                setReloading(true);
              } catch {
                // ignore
              }
            }}
            className={`px-3 py-1.5 text-sm rounded-md border transition-colors ${
              reloading
                ? 'bg-primary-600 text-white border-primary-600 opacity-80'
                : 'bg-primary-600 hover:bg-primary-700 text-white border-primary-600'
            }`}
          >
            {reloading
              ? t('update.reloading')
              : queued
                ? t('update.queued')
                : isCallCritical
                  ? t('update.reloadAfterCall')
                  : t('update.reload')}
          </button>
        </div>
      </div>
    </div>
  );
}
